import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/PhoneInput";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PushToggle } from "@/components/PushToggle";
import { PasskeySettings } from "@/components/PasskeySettings";
import { LoginActivity } from "@/components/LoginActivity";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

function toDataUrl(file, max = 256) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const s = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.src = URL.createObjectURL(file);
  });
}

export function ProfileDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ name: "", phone: "", title: "", avatar: "", check_logo: true });
  useEffect(() => { if (open) setF({ name: user.name || "", phone: user.phone || "", title: user.title || "", avatar: user.avatar || "", check_logo: user.check_logo !== false }); }, [open, user]);

  const save = async () => {
    try {
      const { data } = await api.put("/me/profile", f);
      setUser?.(data);
      toast.success("Profile updated");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="profile-modal">
        <DialogHeader><DialogTitle className="font-display">My Profile</DialogTitle>
          <DialogDescription>{user.role === "bureau_admin" ? "Clients you manage see this contact info on their dashboard." : "Update your contact details."}</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16"><AvatarImage src={f.avatar} /><AvatarFallback>{f.name?.[0]}</AvatarFallback></Avatar>
            <div className="space-y-1 flex-1"><Label className="text-xs">Photo</Label>
              <Input data-testid="profile-avatar-file" type="file" accept="image/*" onChange={async (e) => { const file = e.target.files?.[0]; if (file) setF({ ...f, avatar: await toDataUrl(file) }); }} /></div>
          </div>
          <div className="space-y-1"><Label>Full name</Label><Input data-testid="profile-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          {user.role === "bureau_admin" && <div className="space-y-1"><Label>Title</Label><Input data-testid="profile-title" placeholder="Payroll Manager" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>}
          <div className="space-y-1"><Label>Telephone</Label><PhoneInput data-testid="profile-phone" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} /></div>
          <div className="space-y-1"><Label>Email</Label><Input value={user.email} disabled /></div>
          <PushToggle />
          <PasskeySettings />
          <LoginActivity />
          {user.role !== "employee" && (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div><Label htmlFor="check-logo-toggle">Print company logo on checks</Label><p className="text-xs text-muted-foreground">Shows the client's logo at the top left of MICR payroll checks you print.</p></div>
              <Switch id="check-logo-toggle" data-testid="profile-check-logo" checked={f.check_logo} onCheckedChange={(v) => setF({ ...f, check_logo: v })} />
            </div>
          )}
        </div>
        <DialogFooter><Button data-testid="profile-save" onClick={save} disabled={!f.name}>Save profile</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
